import React from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import { loadEmployeeNotifications } from './actions/load-employee-notifications';
import { updateEmployeeNotification } from './actions/update-employee-notification';
import { loadOrgUsers } from '../action/org/load-org-users';
import { userSelector } from '../reducers/current-user-selectors';
import { employeeNotificationsForOrgIdSelector } from '../reducers/employee-selectors';
import { usersForOrgIdSelector } from '../reducers/user-selectors';

import { UiGreenSwitch } from '../ui/ui-green-switch';

import { EMPLOYEE_PERMISSION } from '../util/permissions';
import { SORT_ORDER, sortAlphabetically } from '../util/sort-functions';
import { LoadingMessage } from '../widgets/loading-message';

interface InputProps {
  orgId: number;
}

const mapStateToProps = (state: any, ownProps: InputProps) => ({
  currentUser: userSelector(state),
  users: usersForOrgIdSelector(state, ownProps.orgId),
  notifications: employeeNotificationsForOrgIdSelector(state, ownProps.orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  ownProps: InputProps,
) => ({
  actions: {
    loadUsers: () => dispatch(loadOrgUsers(ownProps.orgId)),
    loadEmployeeNotifications: () => dispatch(loadEmployeeNotifications(ownProps.orgId)),
    updateEmployeeNotification: (username: string, isOn: boolean) =>
      dispatch(updateEmployeeNotification(ownProps.orgId, username, isOn)),
  },
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

type Props = InputProps & StateProps & DispatchProps;

class EmployeeNotificationsComponent extends React.PureComponent<Props> {
  componentDidMount() {
    this.props.actions.loadUsers();
    this.props.actions.loadEmployeeNotifications();
  }

  /*
    https://fettblog.eu/typescript-react/events/#where%E2%80%99s-inputevent%3F
   */
  updateSubscription = (username: string) => (event: any) => {
    const isOn = event.target.checked;
    this.props.actions.updateEmployeeNotification(username, isOn);
  };

  renderForSupervisor() {
    const { currentUser, notifications } = this.props;

    if (!notifications) {
      return <LoadingMessage />;
    }

    return (
      <div className="u-padding-top-2x">
        <div className="typo-h5 typo-bold">Employee Notification Settings</div>
        <div>
          <UiGreenSwitch
            checked={notifications.find((n) => n.username === currentUser.username) !== undefined}
            onChange={this.updateSubscription(currentUser.username)}
          />
          Receive notifications for incoming employee messages
        </div>
      </div>
    );
  }

  renderForAllPermission() {
    const { users, notifications } = this.props;

    if (!users || !notifications) {
      return <LoadingMessage />;
    }

    return (
      <div className="u-maxWidth-800px u-padding-top-2x">
        <div className="typo-h5 typo-bold">Employee Notification Settings</div>
        <div className="u-margin-bottom-xl">
          Please select at least one person to receive notifications for incoming employee messages.
          Supervisors will only receive notifications for their direct reports.
        </div>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell align="center">Incoming Employee Messages</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users
              .filter((u) => u.isActive)
              .sort(sortAlphabetically((u) => u.firstName, SORT_ORDER.ASC))
              .map((u: any) => (
                <TableRow key={`user-notifications-${u.username}`}>
                  <TableCell>
                    {u.firstName} {u.lastName}{' '}
                    {u.employeesPermission === EMPLOYEE_PERMISSION.SUPERVISOR &&
                      '(only direct reports)'}
                  </TableCell>
                  <TableCell align="center">
                    <UiGreenSwitch
                      onClick={this.updateSubscription(u.username)}
                      checked={notifications.find((n) => n.username === u.username) !== undefined}
                    />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  render() {
    return this.props.currentUser.employeesPermission === EMPLOYEE_PERMISSION.SUPERVISOR
      ? this.renderForSupervisor()
      : this.renderForAllPermission();
  }
}

export const EmployeeNotifications = connect(
  mapStateToProps,
  mapDispatchToProps,
)(EmployeeNotificationsComponent);
